import React, { useEffect, useState, useRef, Component } from 'react';
import { motion } from 'framer-motion';
import Button from '../components/Button';
import { StarIcon, ArrowRightIcon, QuoteIcon, SparklesIcon, AwardIcon, UsersIcon, TrophyIcon, HeartIcon, GlobeIcon } from 'lucide-react';
import { TypeAnimation } from 'react-type-animation';
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
const Testimonials = () => {
  const testimonials = [{
    id: 1,
    quote: 'Cuando confiamos a Grupo Reflex la cobertura del Centenario Mazda en Guatemala, no imaginábamos el impacto que tendrían. No solo fue una producción impecable: cada plano, cada pieza gráfica y cada segundo en pantalla transmitían la filosofía y el espíritu de Mazda. El reconocimiento que recibimos desde Japón como el mejor material de Latinoamérica fue la confirmación de que estábamos trabajando con un equipo que entiende la esencia de la marca. Su narrativa trascendió lo visual; contaron una historia que conmovió a todos.',
    author: 'Sergio Marroquín',
    position: 'Gerente de Marketing Mazda, Grupo Q',
    rating: 5,
    project: 'Cobertura del Centenario Mazda',
    gradient: 'from-purple-600/90 to-pink-600/90'
  }, {
    id: 2,
    quote: 'Recuerdo el lanzamiento de la Peugeot 2008 como un antes y un después para nosotros. Fue la primera vez que apostamos por una producción de gran escala con un equipo local, y Grupo Reflex superó todas las expectativas. Siete días de rodaje, un crew de cine completo, un resultado digno de cualquier campaña internacional. Ese evento no solo marcó la llegada de la SUV 2008, sino también el estándar de cómo queríamos comunicar la marca Peugeot en Guatemala. Fue reconocido como el mejor lanzamiento en Latinoamérica, y con razón.',
    author: 'Williams Figueroa',
    position: 'Gerente de Marketing Tecún Automotores',
    rating: 5,
    project: 'Lanzamiento All New Peugeot 2008',
    gradient: 'from-blue-600/90 to-purple-600/90'
  }, {
    id: 3,
    quote: 'El lanzamiento del nuevo Landtrek fue una aventura en todo sentido. Grupo Reflex no se limitó a producir un video; nos llevó a vivir una historia. Viajamos juntos por tres departamentos, desde la Represa de Chixoy hasta Semuc Champey y Río Dulce. Todo con un equipo reducido, pero con una calidad y una entrega gigantescas. Cuando vimos el material final transmitido a toda Centroamérica, supimos que habíamos creado algo único: un rally visual que capturó el espíritu aventurero del Landtrek y de la marca Peugeot.',
    author: 'Alain de León',
    position: 'Gerente de Marca Peugeot',
    rating: 5,
    project: 'Lanzamiento All New Landtrek',
    gradient: 'from-pink-600/90 to-red-600/90'
  }, {
    id: 4,
    quote: "'Un Minuto desde el Río' nació como una idea y se convirtió en un fenómeno. Grupo Reflex entendió mi visión desde el primer día: queríamos algo que inspirara, que contara historias auténticas de nuestro entorno. Ellos crearon el nombre, el concepto y la serie completa de 15 episodios. En menos de seis meses, nuestras ventas y reservas crecieron más del 300%. Pero más allá de las cifras, lo que lograron fue darle alma a nuestra marca. Cada cápsula conectó con el corazón de la audiencia.",
    author: 'José Cruz',
    position: 'CEO GTP Properties',
    rating: 5,
    project: 'Serie "Un Minuto desde el Río"',
    gradient: 'from-purple-600/90 to-blue-600/90'
  }, {
    id: 5,
    quote: 'Soñábamos con mostrar la belleza de nuestra región y de nuestra cadena hotelera, y Grupo Reflex lo convirtió en una realidad visualmente poderosa. Desde el concepto creativo hasta la producción en locaciones icónicas, todo fue ejecutado con un profesionalismo impresionante. Con tres modelos internacionales, un equipo talentoso y cuatro días intensos de rodaje, lograron elevar la imagen del turismo en Chiquimula. Hoy Caporal H&R y toda nuestra cadena son reconocidas como referentes en hospitalidad. Realmente crearon una campaña que hace honor a su nombre: Chiquimula Impresionante.',
    author: 'Carlos Fernando Heredia',
    position: 'Gerente Hotel Vista Terra',
    rating: 5,
    project: 'Chiquimula Impresionante',
    gradient: 'from-green-600/90 to-teal-600/90'
  }, {
    id: 6,
    quote: 'Cuando nos propusimos lanzar el Plan de Lealtad de la App MiGrupoQ, necesitábamos una producción que reflejara innovación, cercanía y confianza. Grupo Reflex capturó todo eso con una precisión increíble. Desde el storytelling hasta el storyboard, cada detalle fue trabajado con visión estratégica y sentido humano. Los tres videos producidos en distintas locaciones de la capital lograron conectar emocionalmente con nuestros clientes y colaboradores. Fue un proyecto impecable, que no solo fortaleció nuestra marca, sino también nuestra relación con los usuarios.',
    author: 'Katty Corado',
    position: 'Gerente División CRM Grupo Q',
    rating: 5,
    project: 'Plan de Lealtad App MiGrupoQ',
    gradient: 'from-pink-600/90 to-purple-600/90'
  }];
  const [revealedTestimonials, setRevealedTestimonials] = useState<Set<number>>(new Set());
  const handleTestimonialHover = (id: number) => {
    setRevealedTestimonials(prev => new Set(prev).add(id));
  };
  const renderRatingStars = (rating: number) => {
    return Array.from({
      length: 5
    }).map((_, index) => <StarIcon key={index} className={`h-5 w-5 ${index < rating ? 'text-yellow-400 fill-yellow-400' : 'text-gray-300'}`} data-id="element-808" />);
  };
  // Coordenadas de países
  const guatemala = {
    coordinates: [-90.2308, 15.7835],
    name: 'Guatemala'
  };
  // Definir colores para los países
  const colors = {
    celeste: '#06b6d4',
    morado: '#a855f7',
    rosado: '#ec4899'
  };
  const destinations = [{
    coordinates: [-95.7129, 37.0902],
    name: 'Estados Unidos',
    color: colors.morado
  }, {
    coordinates: [-84.0907, 9.7489],
    name: 'Costa Rica',
    color: colors.celeste
  }, {
    coordinates: [-88.8965, 13.7942],
    name: 'El Salvador',
    color: colors.rosado
  }, {
    coordinates: [-86.2419, 14.6349],
    name: 'Honduras',
    color: colors.morado
  }, {
    coordinates: [-85.2072, 12.8654],
    name: 'Nicaragua',
    color: colors.celeste
  }, {
    coordinates: [-80.7821, 8.538],
    name: 'Panamá',
    color: colors.rosado
  }, {
    coordinates: [-88.4976, 17.1899],
    name: 'Belice',
    color: colors.celeste
  }, {
    coordinates: [-77.7812, 21.5218],
    name: 'Cuba',
    color: colors.rosado
  }, {
    coordinates: [-70.1627, 18.7357],
    name: 'República Dominicana',
    color: colors.morado
  }, {
    coordinates: [-47.9292, -15.8267],
    name: 'Brasil',
    color: colors.celeste
  }, {
    coordinates: [-70.6693, -33.4489],
    name: 'Chile',
    color: colors.morado
  }, {
    coordinates: [-56.1645, -34.9011],
    name: 'Uruguay',
    color: colors.rosado
  }, {
    coordinates: [5.2913, 52.1326],
    name: 'Holanda',
    color: colors.rosado
  }, {
    coordinates: [-18.0686, 64.9631],
    name: 'Islandia',
    color: colors.celeste
  }, {
    coordinates: [-6.2597, 54.5973],
    name: 'Irlanda del Norte',
    color: colors.morado
  }, {
    coordinates: [45.0792, 23.8859],
    name: 'Arabia Saudí',
    color: colors.celeste
  }, {
    coordinates: [32.2903, 1.3733],
    name: 'Uganda',
    color: colors.rosado
  }, {
    coordinates: [138.2529, 36.2048],
    name: 'Japón',
    color: colors.morado
  }, {
    coordinates: [127.7669, 35.9078],
    name: 'Corea del Sur',
    color: colors.celeste
  }];
  // Estado para controlar el zoom
  const [zoomLevel, setZoomLevel] = useState(1);
  const [hoveredCountry, setHoveredCountry] = useState<string | null>(null);
  // Componente de marcador con efecto de ondas que se adapta al zoom
  const EpicenterMarker = ({
    coordinates,
    isPrimary = false,
    delay = 0,
    color = colors.morado,
    name = ''
  }: {
    coordinates: [number, number];
    isPrimary?: boolean;
    delay?: number;
    color?: string;
    name?: string;
  }) => {
    const waves = [0, 1, 2];
    // Calcular tamaño base según el zoom
    const baseSize = isPrimary ? 8 / Math.sqrt(zoomLevel) : 5 / Math.sqrt(zoomLevel);
    const waveSize = isPrimary ? 30 / Math.sqrt(zoomLevel) : 20 / Math.sqrt(zoomLevel);
    const isHovered = hoveredCountry === name;
    return <Marker coordinates={coordinates} data-id="element-809">
        <g onMouseEnter={() => !isPrimary && setHoveredCountry(name)} onMouseLeave={() => setHoveredCountry(null)} style={{
        cursor: isPrimary ? 'default' : 'pointer'
      }} data-id="element-810">
          {/* Ondas concéntricas */}
          {waves.map(wave => <motion.circle key={wave} r={0} fill="none" stroke={color} strokeWidth={2 / Math.sqrt(zoomLevel)} initial={{
          r: 0,
          opacity: 0.8
        }} animate={{
          r: [0, waveSize],
          opacity: isHovered ? [0.9, 0.2] : [0.8, 0]
        }} transition={{
          duration: isPrimary ? 3 : 2.5,
          repeat: Infinity,
          delay: delay + wave * (isPrimary ? 1 : 0.8),
          ease: 'easeOut'
        }} data-id="element-811" />)}
          {/* Punto central */}
          <motion.circle r={baseSize} fill={color} initial={{
          scale: 0
        }} animate={{
          scale: isHovered ? [1, 1.5, 1.3] : [1, 1.2, 1]
        }} transition={{
          duration: isHovered ? 0.5 : 2,
          repeat: Infinity,
          delay: delay
        }} data-id="element-812" />
          <circle r={baseSize / 2} fill="#fff" data-id="element-813" />
          {/* Efecto de brillo al hacer hover */}
          {isHovered && <motion.circle r={baseSize} fill={color} initial={{
          scale: 1,
          opacity: 0.6
        }} animate={{
          scale: [1, 2, 1],
          opacity: [0.6, 0, 0.6]
        }} transition={{
          duration: 1,
          repeat: Infinity
        }} data-id="element-814" />}
        </g>
      </Marker>;
  };
  // Crear rutas con puntos intermedios para Japón y Corea del Sur
  const getRoutePoints = (destination: (typeof destinations)[0]) => {
    // Para Japón y Corea del Sur, crear puntos intermedios hacia el este
    if (destination.name === 'Japón') {
      return [guatemala.coordinates, [-30, 20], [30, 30], [80, 35], destination.coordinates];
    }
    if (destination.name === 'Corea del Sur') {
      return [guatemala.coordinates, [-30, 20], [30, 30], [80, 35], destination.coordinates];
    }
    // Para los demás países, línea directa
    return [guatemala.coordinates, destination.coordinates];
  };
  // Logos de clientes
  const clientLogos = [{
    name: 'Syngenta',
    color: 'from-green-500 to-emerald-500'
  }, {
    name: 'Grupo Tecun',
    color: 'from-blue-500 to-cyan-500'
  }, {
    name: 'Hyundai Mazda',
    color: 'from-red-500 to-orange-500'
  }, {
    name: 'Peugeot',
    color: 'from-blue-600 to-indigo-600'
  }, {
    name: 'Grupo Q',
    color: 'from-orange-500 to-yellow-500'
  }, {
    name: 'Grupo Caporal',
    color: 'from-purple-500 to-pink-500'
  }, {
    name: 'Expobienes',
    color: 'from-cyan-500 to-blue-500'
  }, {
    name: 'Grupo Abiatar',
    color: 'from-indigo-500 to-purple-500'
  }, {
    name: 'Transportes Kevy',
    color: 'from-teal-500 to-green-500'
  }, {
    name: "Charlene's Project",
    color: 'from-pink-500 to-rose-500'
  }, {
    name: 'Residenciales Stanza',
    color: 'from-violet-500 to-purple-500'
  }, {
    name: 'LEO',
    color: 'from-amber-500 to-orange-500'
  }, {
    name: 'AgroTribo',
    color: 'from-lime-500 to-green-500'
  }, {
    name: 'CATS',
    color: 'from-yellow-500 to-amber-500'
  }, {
    name: 'Zoomlion',
    color: 'from-red-600 to-orange-600'
  }];
  // Hook personalizado para el contador animado
  const useCountUp = (end: number, duration: number = 2000, isVisible: boolean = false) => {
    const [count, setCount] = useState(0);
    const countRef = useRef(0);
    useEffect(() => {
      if (!isVisible) return;
      const endNum = typeof end === 'string' ? parseInt(end) : end;
      const increment = 3;
      const steps = Math.ceil(endNum / increment);
      const stepDuration = duration / steps;
      const timer = setInterval(() => {
        countRef.current += increment;
        if (countRef.current >= endNum) {
          setCount(endNum);
          clearInterval(timer);
        } else {
          setCount(countRef.current);
        }
      }, stepDuration);
      return () => clearInterval(timer);
    }, [end, duration, isVisible]);
    return count;
  };
  // Estado para controlar la visibilidad de las estadísticas
  const [statsVisible, setStatsVisible] = useState(false);
  const statsRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {
        setStatsVisible(true);
      }
    }, {
      threshold: 0.3
    });
    if (statsRef.current) {
      observer.observe(statsRef.current);
    }
    return () => {
      if (statsRef.current) {
        observer.unobserve(statsRef.current);
      }
    };
  }, []);
  return <>
      {/* Hero Section - Full Screen */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-id="element-815">
        <div className="absolute inset-0" data-id="element-816">
          <img src="https://images.unsplash.com/photo-1552664730-d307ca884978?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Testimonios" className="w-full h-full object-cover" data-id="element-817" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/90 via-purple-900/85 to-gray-900/90" data-id="element-818"></div>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1
      }} className="container mx-auto px-4 md:px-6 relative z-10 text-center" data-id="element-819">
          <div className="flex items-center justify-center mb-6" data-id="element-820">
            <QuoteIcon className="h-12 w-12 text-purple-400 mr-4" data-id="element-821" />
            <h1 className="text-5xl md:text-7xl font-bold text-white" data-id="element-822">
              Testimonios
            </h1>
          </div>
          <p className="text-xl md:text-2xl text-gray-300 max-w-3xl mx-auto" data-id="element-823">
            Historias reales de{' '}
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-semibold" data-id="element-824">
              clientes satisfechos
            </span>
          </p>
        </motion.div>
        {/* Scroll Indicator */}
        <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} transition={{
        delay: 1
      }} className="absolute bottom-10 left-1/2 -translate-x-1/2" data-id="element-825">
          <motion.div animate={{
          y: [0, 10, 0]
        }} transition={{
          duration: 2,
          repeat: Infinity
        }} className="text-white" data-id="element-826">
            <ArrowRightIcon className="h-8 w-8 rotate-90" data-id="element-827" />
          </motion.div>
        </motion.div>
      </section>
      {/* Testimonials Grid with Typing Effect */}
      <section className="py-32 bg-gray-900" data-id="element-828">
        <div className="container mx-auto px-4 md:px-6" data-id="element-829">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="text-center mb-20" data-id="element-830">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" data-id="element-831">
              Lo que dicen{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-832">
                nuestros clientes
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-833">
              Cada testimonio es una historia de éxito compartida
            </p>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-id="element-834">
            {testimonials.map((testimonial, index) => <motion.div key={testimonial.id} initial={{
            opacity: 0,
            y: 50
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.8,
            delay: index * 0.1
          }} viewport={{
            once: true
          }} onMouseEnter={() => handleTestimonialHover(testimonial.id)} className="group relative bg-gray-800 rounded-2xl overflow-hidden hover:shadow-2xl hover:shadow-purple-500/20 transition-all duration-500" data-id="element-835">
                <div className={`absolute inset-0 bg-gradient-to-r ${testimonial.gradient} opacity-0 group-hover:opacity-10 transition-opacity duration-500`} data-id="element-836"></div>
                <div className="relative p-8 flex flex-col justify-between min-h-[400px]" data-id="element-837">
                  <div data-id="element-838">
                    <div className="flex mb-6" data-id="element-839">
                      {renderRatingStars(testimonial.rating)}
                    </div>
                    <div className="text-gray-300 text-lg mb-6 min-h-[180px]" data-id="element-840">
                      {revealedTestimonials.has(testimonial.id) ? <TypeAnimation sequence={[testimonial.quote]} wrapper="p" speed={80} cursor={false} className="italic leading-relaxed" data-id="element-841" /> : <p className="italic opacity-30 text-sm" data-id="element-842">
                          Pasa el cursor para leer el testimonio...
                        </p>}
                    </div>
                  </div>
                  <div data-id="element-843">
                    <div className="flex items-center mb-4" data-id="element-844">
                      <QuoteIcon className="h-8 w-8 text-purple-400 mr-3" data-id="element-845" />
                      <div data-id="element-846">
                        <h3 className="text-xl font-bold text-white" data-id="element-847">
                          {testimonial.author}
                        </h3>
                        <p className="text-purple-400 font-semibold" data-id="element-848">
                          {testimonial.position}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center text-sm text-gray-500 pt-4 border-t border-gray-700" data-id="element-849">
                      <SparklesIcon className="h-4 w-4 mr-2" data-id="element-850" />
                      <span data-id="element-851">{testimonial.project}</span>
                    </div>
                  </div>
                </div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Global Reach Map Section */}
      <section className="relative py-32 bg-gray-900 overflow-hidden" data-id="element-852">
        <div className="absolute inset-0 opacity-5" data-id="element-853">
          <div className="absolute top-0 left-0 w-96 h-96 bg-purple-500 rounded-full blur-3xl" data-id="element-854"></div>
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-pink-500 rounded-full blur-3xl" data-id="element-855"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-856">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-857">
            <div className="flex items-center justify-center mb-6" data-id="element-858">
              <GlobeIcon className="h-12 w-12 text-purple-400 mr-4" data-id="element-859" />
              <h2 className="text-4xl md:text-5xl font-bold text-white" data-id="element-860">
                Alcance Global
              </h2>
            </div>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-861">
              Nuestros proyectos han llegado a{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent font-semibold" data-id="element-862">
                más de 17 países
              </span>{' '}
              alrededor del mundo
            </p>
          </motion.div>
          <motion.div initial={{
          opacity: 0,
          scale: 0.9
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} transition={{
          duration: 1
        }} viewport={{
          once: true
        }} className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-8 shadow-2xl" data-id="element-863">
            <ComposableMap projection="geoMercator" projectionConfig={{
            scale: 100,
            center: [0, 20]
          }} className="w-full h-[600px]" data-id="element-864">
              <ZoomableGroup zoom={1} center={[0, 20]} minZoom={1} maxZoom={8} onMoveEnd={position => {
              setZoomLevel(position.zoom);
            }} data-id="element-865">
                <Geographies geography="https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json" data-id="element-866">
                  {({
                  geographies
                }) => geographies.map(geo => {
                  // Verificar si el país está en nuestra lista
                  const isDestination = destinations.some(dest => geo.properties.name === dest.name);
                  const destination = destinations.find(dest => geo.properties.name === dest.name);
                  const isHovered = hoveredCountry === geo.properties.name;
                  return <Geography key={geo.rsmKey} geography={geo} fill={isHovered && destination ? destination.color : '#1f2937'} stroke="#374151" strokeWidth={0.5} style={{
                    default: {
                      outline: 'none'
                    },
                    hover: {
                      fill: isDestination && destination ? destination.color : '#374151',
                      outline: 'none'
                    },
                    pressed: {
                      fill: '#4b5563',
                      outline: 'none'
                    }
                  }} data-id="element-867" />;
                })}
                </Geographies>
                {/* Marcador principal de Guatemala */}
                <EpicenterMarker coordinates={guatemala.coordinates as [number, number]} isPrimary={true} delay={0} color={colors.morado} name={guatemala.name} data-id="element-868" />
                {/* Marcadores de destinos */}
                {destinations.map((destination, index) => <EpicenterMarker key={destination.name} coordinates={destination.coordinates as [number, number]} isPrimary={false} delay={index * 0.2} color={destination.color} name={destination.name} data-id="element-869" />)}
              </ZoomableGroup>
            </ComposableMap>
            <div className="mt-6 text-center" data-id="element-870">
              <p className="text-gray-400 text-sm" data-id="element-871">
                💡 Usa la rueda del mouse o pellizca para hacer zoom en el mapa.
                Pasa el cursor sobre los países para verlos iluminados.
              </p>
            </div>
          </motion.div>
          {/* Lista de países */}
          <motion.div initial={{
          opacity: 0,
          y: 30
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8,
          delay: 0.3
        }} viewport={{
          once: true
        }} className="mt-12 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4" data-id="element-872">
            {[guatemala, ...destinations].map((destination, index) => <motion.div key={destination.name} initial={{
            opacity: 0,
            y: 20
          }} whileInView={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} viewport={{
            once: true
          }} className="bg-gray-800/50 backdrop-blur-sm rounded-lg p-3 text-center hover:bg-purple-900/30 transition-colors duration-300" style={{
            borderLeft: `3px solid ${destination.color || colors.morado}`
          }} data-id="element-873">
                <p className="text-sm text-gray-300" data-id="element-874">{destination.name}</p>
              </motion.div>)}
          </motion.div>
        </div>
      </section>
      {/* Client Logos Section */}
      <section className="py-32 bg-gradient-to-b from-gray-900 to-gray-800" data-id="element-875">
        <div className="container mx-auto px-4 md:px-6" data-id="element-876">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.8
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-877">
            <h2 className="text-4xl md:text-5xl font-bold mb-6 text-white" data-id="element-878">
              Marcas que{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-879">
                confían en nosotros
              </span>
            </h2>
            <p className="text-xl text-gray-400 max-w-3xl mx-auto" data-id="element-880">
              Empresas líderes que han elegido transformar su visión con Grupo
              Reflex
            </p>
          </motion.div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-8" data-id="element-881">
            {clientLogos.map((client, index) => <motion.div key={index} initial={{
            opacity: 0,
            scale: 0.8
          }} whileInView={{
            opacity: 1,
            scale: 1
          }} transition={{
            duration: 0.5,
            delay: index * 0.05
          }} viewport={{
            once: true
          }} className="group relative bg-gray-800/50 backdrop-blur-sm rounded-xl p-8 hover:bg-gray-800 transition-all duration-300 flex items-center justify-center min-h-[120px]" data-id="element-882">
                <div className={`text-center bg-gradient-to-r ${client.color} bg-clip-text text-transparent`} data-id="element-883">
                  <p className="text-2xl font-bold group-hover:scale-110 transition-transform duration-300" data-id="element-884">
                    {client.name}
                  </p>
                </div>
                <div className={`absolute inset-0 bg-gradient-to-r ${client.color} opacity-0 group-hover:opacity-10 rounded-xl transition-opacity duration-300`} data-id="element-885"></div>
              </motion.div>)}
          </div>
        </div>
      </section>
      {/* Statistics Section with Parallax */}
      <section className="relative py-32" ref={statsRef} data-id="element-886">
        <div className="absolute inset-0" data-id="element-887">
          <img src="https://images.unsplash.com/photo-1600880292203-757bb62b4baf?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Stats" className="w-full h-full object-cover" data-id="element-888" />
          <div className="absolute inset-0 bg-gradient-to-r from-gray-900/95 via-purple-900/80 to-gray-900/95" data-id="element-889"></div>
        </div>
        <div className="container mx-auto px-4 md:px-6 relative z-10" data-id="element-890">
          <motion.div initial={{
          opacity: 0,
          y: 50
        }} whileInView={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 1
        }} viewport={{
          once: true
        }} className="text-center mb-16" data-id="element-891">
            <h2 className="text-4xl md:text-5xl font-bold text-white mb-6" data-id="element-892">
              Números que{' '}
              <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-893">
                hablan por sí mismos
              </span>
            </h2>
          </motion.div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8" data-id="element-894">
            <StatCard icon={<HeartIcon className="h-12 w-12" data-id="element-896" />} value={97} suffix="%" label="Clientes satisfechos" index={0} isVisible={statsVisible} data-id="element-895" />
            <StatCard icon={<UsersIcon className="h-12 w-12" data-id="element-898" />} value={150} suffix="+" label="Proyectos completados" index={1} isVisible={statsVisible} data-id="element-897" />
            <StatCard icon={<TrophyIcon className="h-12 w-12" data-id="element-900" />} value={10} suffix="+" label="Años de experiencia" index={2} isVisible={statsVisible} data-id="element-899" />
            <StatCard icon={<AwardIcon className="h-12 w-12" data-id="element-902" />} value={24} suffix="" label="Premios ganados" index={3} isVisible={statsVisible} data-id="element-901" />
          </div>
        </div>
      </section>
      {/* CTA Section - Full Screen */}
      <section className="relative h-screen flex items-center justify-center" data-id="element-903">
        <div className="absolute inset-0" data-id="element-904">
          <img src="https://images.unsplash.com/photo-1521737711867-e3b97375f902?ixlib=rb-4.0.3&auto=format&fit=crop&w=2070&q=80" alt="Contact" className="w-full h-full object-cover" data-id="element-905" />
          <div className="absolute inset-0 bg-gradient-to-b from-gray-900/80 via-purple-900/90 to-gray-900/80" data-id="element-906"></div>
        </div>
        <motion.div initial={{
        opacity: 0,
        y: 50
      }} whileInView={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 1
      }} viewport={{
        once: true
      }} className="container mx-auto px-4 md:px-6 text-center relative z-10" data-id="element-907">
          <h2 className="text-4xl md:text-6xl font-bold mb-6 text-white" data-id="element-908">
            ¿Listo para ser nuestro
            <br data-id="element-909" />
            <span className="bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent" data-id="element-910">
              próximo cliente satisfecho?
            </span>
          </h2>
          <p className="text-xl md:text-2xl text-gray-300 mb-12 max-w-3xl mx-auto" data-id="element-911">
            Únete a las empresas que ya confían en nosotros para transformar sus
            ideas en realidad
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4" data-id="element-912">
            <Button href="/contacto" variant="primary" size="lg" className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-lg px-8 py-4" data-id="element-913">
              Comienza tu proyecto
              <ArrowRightIcon className="ml-2 h-5 w-5" data-id="element-914" />
            </Button>
            <Button href="/proyectos" variant="outline" size="lg" className="border-2 border-white text-white hover:bg-white hover:text-gray-900 text-lg px-8 py-4" data-id="element-915">
              Ver proyectos
            </Button>
          </div>
        </motion.div>
      </section>
    </>;
};
// Componente separado para cada tarjeta de estadística
const StatCard = ({
  icon,
  value,
  suffix,
  label,
  index,
  isVisible
}: {
  icon: React.ReactNode;
  value: number;
  suffix: string;
  label: string;
  index: number;
  isVisible: boolean;
}) => {
  const [count, setCount] = useState(0);
  useEffect(() => {
    if (!isVisible) return;
    const increment = 3;
    const steps = Math.ceil(value / increment);
    const duration = 2000;
    const stepDuration = duration / steps;
    let currentCount = 0;
    const timer = setInterval(() => {
      currentCount += increment;
      if (currentCount >= value) {
        setCount(value);
        clearInterval(timer);
      } else {
        setCount(currentCount);
      }
    }, stepDuration);
    return () => clearInterval(timer);
  }, [value, isVisible]);
  return <motion.div initial={{
    opacity: 0,
    scale: 0.8
  }} whileInView={{
    opacity: 1,
    scale: 1
  }} transition={{
    duration: 0.8,
    delay: index * 0.1
  }} viewport={{
    once: true
  }} className="text-center group" data-id="element-916">
      <div className="inline-flex items-center justify-center w-24 h-24 rounded-full bg-gradient-to-br from-purple-600 to-pink-600 text-white mb-6 group-hover:scale-110 transition-transform duration-300" data-id="element-917">
        {icon}
      </div>
      <div className="text-5xl md:text-6xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent mb-4" data-id="element-918">
        {count}
        {suffix}
      </div>
      <p className="text-xl text-gray-300" data-id="element-919">{label}</p>
    </motion.div>;
};
export default Testimonials;